"use client";

import React, { useMemo, useState, useRef, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Card, CardContent, Typography, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  CartesianGrid,
} from "recharts";

interface TimeWiseUtilizationGraphProps {
  startDate: string;
  endDate: string;
  fleetId?: string;
  tableGraphData?: any;
}

const TimeWiseUtilizationGraph: React.FC<TimeWiseUtilizationGraphProps> = ({
  startDate,
  endDate,
  fleetId,
  tableGraphData,
}) => {
  const theme = useTheme();

  // Sort data in ascending order (oldest first)
  const sortedData = useMemo(() => {
    if (!tableGraphData || !tableGraphData.timeWiseFleetUtilizationList) {
      return [];
    }
    return [...tableGraphData.timeWiseFleetUtilizationList].sort(
      (a, b) =>
        new Date(a.date.replace(/_/g, "-")).getTime() -
        new Date(b.date.replace(/_/g, "-")).getTime()
    );
  }, [tableGraphData]);

  const windowStart = useRef(0);
  const [processedData, setProcessedData] = useState<{ name: string; value: number }[]>([]);
  const [dataRangeLabel, setDataRangeLabel] = useState("");
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  // When sortedData changes, set windowStart to show last 6 days.
  useEffect(() => {
    if (sortedData.length > 0) {
      windowStart.current =
        sortedData.length > 6 ? sortedData.length - 6 : 0;
    } else {
      windowStart.current = 0;
    }
    updateChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData]);

  // Helper function to update processedData and dataRangeLabel.
  const updateChartData = () => {
    if (sortedData.length > 0) {
      const start = windowStart.current;
      const currentWindow = sortedData.slice(start, start + 6);

      // Compute the date range label.
      const startDateObj = new Date(currentWindow[0].date.replace(/_/g, "-"));
      const endDateObj = new Date(
        currentWindow[currentWindow.length - 1].date.replace(/_/g, "-")
      );
      const startLabel = startDateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
      const endLabel = endDateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });
      const rangeLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
      setDataRangeLabel(rangeLabel);

      // Process the window data to the expected chart format.
      const transformedData = currentWindow.map((item) => {
        // Expecting date format "YYYY_MM_DD" or "YYYY-MM-DD"
        let dateStr = item.date.replace(/_/g, "-");
        const dateObj = new Date(dateStr);
        const day = dateObj.getDate().toString().padStart(2, "0");
        const month = dateObj.toLocaleString("default", { month: "short" });
        return {
          name: `${day} ${month}`,
          value: Math.round(item.timeBasedUtilization),
        };
      });
      setProcessedData(transformedData);

      // Set navigation flags.
      setHasPrevious(start > 0);
      setHasNext(start + 6 < sortedData.length);
    } else {
      setProcessedData([]);
      setDataRangeLabel("");
      setHasPrevious(false);
      setHasNext(false);
    }
  };

  const handlePrevious = () => {
    if (hasPrevious) {
      windowStart.current = Math.max(0, windowStart.current - 1);
      updateChartData();
    }
  };

  const handleNext = () => {
    if (hasNext) {
      windowStart.current = Math.min(
        sortedData.length - 6,
        windowStart.current + 1
      );
      updateChartData();
    }
  };

  return (
    <Card className="w-full h-full flex flex-col items-start">
      <Typography
        variant="button"
        color="secondary"
        fontWeight={400}
        className="mb-2"
      >
        Time Wise Fleet Utilization Graph
      </Typography>
      <CardContent className="border rounded-xl border-[#F0EFF8] w-full">
        <div className="flex gap-2 pt-4">
          <div className="relative" style={{ left: "8px", top: "105px" }}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                textAlign: "center",
              }}
            >
              Utilization (%)
            </Typography>
          </div>
          <div className="flex-1 min-h-[200px] max-h-[400px]">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={processedData}>
                <defs>
                  <linearGradient id="lineGradient" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor={`${theme.palette.primary.main}00`} />
                    <stop offset="25%" stopColor={`${theme.palette.primary.main}40`} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" />
                <YAxis />
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip
                  cursor={false}
                  contentStyle={{
                    background: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  }}
                  labelStyle={{ color: theme.palette.text.primary }}
                  itemStyle={{ color: theme.palette.text.primary }}
                  formatter={(value: number, name: string) => [`${value}%`, "Utilization"]}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  fill="url(#lineGradient)"
                  fillOpacity={1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        {sortedData.length > 0 ? (
          <div className="w-full flex items-center justify-center gap-4 mt-2">
            <IconButton onClick={handlePrevious} disabled={!hasPrevious} size="small">
              <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
            <Typography variant="caption" color="textPrimary" align="center">
              {dataRangeLabel}
            </Typography>
            <IconButton onClick={handleNext} disabled={!hasNext} size="small">
              <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </div>
        ) : (
          <div className="w-full flex justify-center">
            <Typography variant="caption" color="textPrimary" align="center" className="mt-2 w-full">
              Fleet Utilization Data
            </Typography>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TimeWiseUtilizationGraph;